import { useRef } from 'react';
import {
  useExitTransition,
  type UseExitTransitionArgs,
} from './useExitTransition';

export interface UseExitRetainedResult<T> {
  /** The value; while the exit runs, the last one it had. */
  readonly value: T | null;
  /** The exit is running: the thing is on its way out, and what it shows must stay as it was. */
  readonly exiting: boolean;
}

/**
 * The value a thing was showing when it was last wanted, held while it animates out. A caller that clears the value in
 * the same turn it stops wanting the thing (a panel given `source={null}` on close) would otherwise have the panel empty
 * or swap to its loading state while it slides away.
 */
export function useExitRetained<T>(
  value: T | null,
  wanted: boolean,
  options: Pick<UseExitTransitionArgs, 'size' | 'durationMs'> = {}
): UseExitRetainedResult<T> {
  const last = useRef<T | null>(null);
  const { mounted } = useExitTransition({ open: wanted, ...options });
  if (wanted && value !== null) {
    last.current = value;
  }
  const exiting = mounted && !wanted;
  return { value: exiting ? last.current : value, exiting };
}
